import { ReactElement, Fragment, useCallback } from 'react';
import { AvatarGroup, Box, Stack, Td, Tr } from '@ff-it/ui';
import type { BlockDetails } from 'modules/campaign/block/types';
import {
  BoundStates,
  useEntityActions,
  useRequestActions,
  usePromiseActions,
  ActionDropdown,
  EntityContextType,
  BlockWidget,
} from 'components';
import type { CampaignDetails } from '../types';
import { useManagerActions } from 'modules/campaign/block/BlockActions/useManagerActions';
import { sepFormat } from 'utilities';
import { BlockNotice } from 'modules/campaign/block/BlockNotice';
import { fetcher as defaultFethcer, Fetcher } from 'services';
import { RequestArgs } from '@ff-it/api';
import { UserAvatar } from 'components/UserAvatar';
import { PeriodWidget } from 'components/PeriodWidget';
import { DeadlineDate } from 'components/DeadlineDate';

export interface BlockControlProps {
  itemContext: EntityContextType<BlockDetails>;
  campaign: CampaignDetails;
  remove: (block: BlockDetails) => void;
  add: (block: BlockDetails) => void;
}

function BlockControls({ itemContext, campaign, remove, add }: BlockControlProps): ReactElement | null {
  const fetcher: Fetcher = useCallback(
    ({ headers, ...args }: RequestArgs) =>
      defaultFethcer({
        headers: {
          ...headers,
          'X-If-Revision': itemContext.item._revision.toString(),
        },
        ...args,
      }),
    [itemContext.item._revision],
  );
  const actions = usePromiseActions(
    useRequestActions(
      useEntityActions(
        itemContext,
        useManagerActions({
          fetcher,
          campaign,
          onAdd: add,
          onRemove: remove,
        }),
      ),
      fetcher,
    ),
    itemContext.setSpinning,
    itemContext.setItem,
  );
  if (actions.length === 0) {
    return null;
  }

  return (
    <ActionDropdown
      actions={actions}
      variant="ghost"
      icon="ellipsis-vertical"
      testId="block-overview-actions"
      menuSize="md"
    />
  );
}

export function Block(props: BlockControlProps): ReactElement {
  const block = props.itemContext.item;

  const {
    id,
    date_from,
    date_to,
    deadline,
    sums,
    planners,
    budget,
    role: { manage, plan },
    final_client_po_number,
  } = block;

  const notice = <BlockNotice {...block} overview />;
  return (
    <Fragment key={id}>
      <Tr className="block-overview" data-block-id={id} data-role-plan={plan} data-role-manage={manage}>
        <Td className="controls" rowSpan={2}>
          {manage && <BlockControls {...props} />}
        </Td>
        <Td className="state">{sums && <BoundStates {...sums} />}</Td>
        <Td className="block">
          <Box display="flex" justifyContent="space-between" alignItems="center" minWidth="0" overflow="hidden">
            <BlockWidget {...block} campaign_id={props.campaign.id} target="_self" link />
            {final_client_po_number && (
              <small className="text-muted" data-testid="client-po-number">
                PO: {final_client_po_number}
              </small>
            )}
          </Box>
        </Td>

        <Td className="period">
          <Stack direction="vertical" textSize="xs" lineHeight="1" space="0" minWidth="0" overflow="hidden">
            <DeadlineDate deadline={deadline} />
            <PeriodWidget date_from={date_from} date_to={date_to} distance />
          </Stack>
        </Td>
        <Td className="planners">
          <AvatarGroup size="xs">
            {planners.map((user) => (
              <UserAvatar user={user} key={user.id} />
            ))}
          </AvatarGroup>
        </Td>
        <Td textAlign="end">{sepFormat(budget)}</Td>
        <Td textAlign="end" className="client">
          {sepFormat(sums?.income_total)}
        </Td>
        <Td textAlign="end" className="agency">
          {sepFormat(sums?.expense_total)}
        </Td>
        <Td textAlign="end" className="revenue">
          {sepFormat(sums?.revenue)}
        </Td>
      </Tr>
      <Tr className="block-notice">
        <Td flush colSpan={8}>
          {notice}
        </Td>
      </Tr>
    </Fragment>
  );
}
